import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Select,
  Statistic,
  Row,
  Col,
  Typography,
  Modal,
  Input,
  message,
  Empty,
  Progress,
  Tooltip
} from 'antd'
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  BookOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { getTaskList, startTask, submitTask, requestExtension, type WritingTask } from '../../api/textbook'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const { Option } = Select
const { Title, Text } = Typography
const { TextArea } = Input

const TaskList: React.FC = () => {
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<WritingTask[]>([])
  const [loading, setLoading] = useState(false)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0
  })
  const [extensionModalVisible, setExtensionModalVisible] = useState(false)
  const [extensionReason, setExtensionReason] = useState('')
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null)

  useEffect(() => {
    loadTasks()
  }, [filterStatus])

  const loadTasks = async () => {
    try {
      setLoading(true)
      const params: any = {}
      if (filterStatus !== 'all') {
        params.status = filterStatus
      }
      
      const response = await getTaskList(params)
      const taskList = response.rows || []
      setTasks(taskList)
      
      // 计算统计信息
      const now = dayjs()
      const statsData = {
        total: taskList.length,
        pending: taskList.filter(t => t.status === 'pending').length,
        inProgress: taskList.filter(t => t.status === 'in_progress').length,
        completed: taskList.filter(t => t.status === 'completed').length,
        overdue: taskList.filter(t => {
          if (t.status === 'completed' || t.status === 'cancelled') return false
          if (!t.deadline) return false
          return dayjs(t.deadline).isBefore(now)
        }).length
      }
      setStats(statsData)
    } catch (error: any) {
      console.error('加载任务列表失败:', error)
      message.error('加载任务列表失败: ' + (error.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  const handleStartTask = async (taskId: number) => {
    try {
      await startTask(taskId)
      message.success('任务已开始')
      loadTasks()
    } catch (error: any) {
      message.error('开始任务失败: ' + (error.message || '未知错误'))
    }
  }

  const handleSubmitTask = async (taskId: number) => {
    Modal.confirm({
      title: '确认提交',
      content: '确定要提交此任务吗？提交后将无法修改。',
      onOk: async () => {
        try {
          await submitTask(taskId)
          message.success('任务已提交')
          loadTasks()
        } catch (error: any) {
          message.error('提交任务失败: ' + (error.message || '未知错误'))
        }
      }
    })
  }

  const handleRequestExtension = (taskId: number) => {
    setCurrentTaskId(taskId)
    setExtensionReason('')
    setExtensionModalVisible(true)
  }

  const confirmExtension = async () => {
    if (!currentTaskId) return
    if (!extensionReason.trim()) {
      message.warning('请输入延期原因')
      return
    }
    
    try {
      await requestExtension(currentTaskId, extensionReason)
      message.success('延期申请已提交')
      setExtensionModalVisible(false)
      setExtensionReason('')
      setCurrentTaskId(null)
      loadTasks()
    } catch (error: any) {
      message.error('申请延期失败: ' + (error.message || '未知错误'))
    }
  }

  const getStatusTag = (status: string, deadline?: string) => {
    const now = dayjs()
    const isOverdue = deadline && dayjs(deadline).isBefore(now) && status !== 'completed' && status !== 'cancelled'
    
    if (isOverdue) {
      return <Tag color="red" icon={<ExclamationCircleOutlined />}>已逾期</Tag>
    }
    
    switch (status) {
      case 'pending':
        return <Tag color="orange" icon={<ClockCircleOutlined />}>待开始</Tag>
      case 'in_progress':
        return <Tag color="blue" icon={<PlayCircleOutlined />}>进行中</Tag>
      case 'completed':
        return <Tag color="green" icon={<CheckCircleOutlined />}>已完成</Tag>
      case 'cancelled':
        return <Tag color="default">已取消</Tag>
      default:
        return <Tag>{status}</Tag>
    }
  }

  const getPriorityTag = (priority: string) => {
    const priorityMap: Record<string, { color: string; text: string }> = {
      urgent: { color: 'red', text: '紧急' },
      high: { color: 'orange', text: '高' },
      normal: { color: 'blue', text: '普通' },
      low: { color: 'default', text: '低' }
    }
    const config = priorityMap[priority] || { color: 'default', text: priority }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  const columns = [
    {
      title: '任务标题',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      width: 250,
      render: (text: string, record: WritingTask) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.textbookTitle && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <BookOutlined /> {record.textbookTitle}
              {record.chapterTitle && ` - ${record.chapterTitle}`}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: '任务类型',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 100,
      render: (type: string) => {
        const typeMap: Record<string, string> = {
          writing: '编写',
          review: '审核',
          revise: '修订'
        }
        return <Tag>{typeMap[type] || type}</Tag>
      }
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => getPriorityTag(priority)
    },
    {
      title: '状态',
      key: 'status',
      width: 120,
      render: (_: any, record: WritingTask) => getStatusTag(record.status, record.deadline)
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number, record: WritingTask) => (
        <Progress
          percent={progress}
          size="small"
          status={record.status === 'completed' ? 'success' : 'active'}
        />
      )
    },
    {
      title: '截止时间',
      dataIndex: 'deadline',
      key: 'deadline',
      width: 180,
      render: (deadline: string, record: WritingTask) => {
        if (!deadline) return '-'
        const deadlineDate = dayjs(deadline)
        const now = dayjs()
        const isOverdue = deadlineDate.isBefore(now) && record.status !== 'completed' && record.status !== 'cancelled'
        
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
              {deadlineDate.format('YYYY-MM-DD HH:mm')}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {isOverdue ? '已逾期' : deadlineDate.fromNow()}
            </Text>
          </Space>
        )
      }
    },
    {
      title: '分配者',
      dataIndex: 'assignerName',
      key: 'assignerName',
      width: 120
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: WritingTask) => {
        const isOverdue = record.deadline && dayjs(record.deadline).isBefore(dayjs()) && 
                         record.status !== 'completed' && record.status !== 'cancelled'
        
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`/textbook/tasks/${record.taskId}`)}
            >
              查看详情
            </Button>
            {record.status === 'pending' && (
              <Button
                type="link"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleStartTask(record.taskId)}
              >
                开始
              </Button>
            )}
            {record.status === 'in_progress' && (
              <>
                <Button
                  type="link"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleSubmitTask(record.taskId)}
                >
                  提交
                </Button>
                {isOverdue && (
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={() => handleRequestExtension(record.taskId)}
                  >
                    申请延期
                  </Button>
                )}
              </>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <FileTextOutlined style={{ marginRight: 8 }} />
        我的任务
      </Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="总任务数" value={stats.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待开始"
              value={stats.pending}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="进行中"
              value={stats.inProgress}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已完成"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {stats.overdue > 0 && (
        <Card style={{ marginBottom: 24, borderColor: '#ff4d4f' }}>
          <Statistic
            title="已逾期任务"
            value={stats.overdue}
            valueStyle={{ color: '#ff4d4f' }}
            prefix={<ExclamationCircleOutlined />}
          />
        </Card>
      )}

      {/* 筛选和任务列表 */}
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <span>状态筛选：</span>
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: 150 }}
          >
            <Option value="all">全部</Option>
            <Option value="pending">待开始</Option>
            <Option value="in_progress">进行中</Option>
            <Option value="completed">已完成</Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="taskId"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          locale={{
            emptyText: <Empty description="暂无任务" />
          }}
        />
      </Card>

      {/* 延期申请弹窗 */}
      <Modal
        title="申请延期"
        open={extensionModalVisible}
        onOk={confirmExtension}
        onCancel={() => {
          setExtensionModalVisible(false)
          setExtensionReason('')
          setCurrentTaskId(null)
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>请输入延期原因：</Text>
        </div>
        <TextArea
          rows={4}
          value={extensionReason}
          onChange={(e) => setExtensionReason(e.target.value)}
          placeholder="请详细说明需要延期的原因..."
        />
      </Modal>
    </div>
  )
}

export default TaskList

